import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
    class: "flex items-start gap-4",
    "data-testid": "consent-coordinate-row"
};
const _hoisted_2 = { class: "flex min-w-0 flex-1 items-center gap-5" };
const _hoisted_3 = { class: "flex min-w-0 flex-1 flex-col gap-1" };
const _hoisted_4 = { class: "flex flex-wrap items-center gap-2" };
const _hoisted_5 = { class: "flex min-w-0 flex-col gap-1.5" };
const _hoisted_6 = ["title"];
const _hoisted_7 = { class: "flex shrink-0 items-center gap-3" };
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Badge, Button, Dropdown, IconShape } from "@eudonet/opal";
import ConsentPurposeBadgeLine from "@/modules/consent/presentation/components/ConsentPurposeBadgeLine.vue";
import { CONSENT_ADDRESS_TYPE_ICON, toDisplayedPurposes, } from "@/modules/consent/domain/entities/consentOverview";
/**
 * One communication address row of the consents tab — the Figma "Interactions item".
 *
 * Page-specific rather than an OPAL component: OPAL's `ListItemCard` models a title/subtitle row
 * with badge and action slots, while this anatomy is a qualifier row, a bold value and a wrapping
 * badge line stacked in one column. Composed entirely from OPAL primitives; if a second consumer
 * appears, the row itself is what should move to OPAL.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'ConsentCoordinateRow',
    props: {
        channelType: {},
        value: {},
        qualifier: {},
        isPrimary: { type: Boolean },
        purposes: {},
        canEdit: { type: Boolean }
    },
    emits: ["edit", "view-history"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const addressIcon = computed(() => CONSENT_ADDRESS_TYPE_ICON[props.channelType]);
        /**
         * What the badge line actually shows: refusals dropped, granted purposes first — the same rule the
         * record-view widget applies, from the same helper. The row summarises what may be sent to this
         * address; a refusal is read from the history or the edit modal, not from the summary.
         */
        const displayedPurposes = computed(() => toDisplayedPurposes(props.purposes));
        // Only the history entry today. `Voir l'historique` is the history story's surface; it is wired
        // here because the design puts it in this menu and the tab already owns a working drawer —
        // removing it while rebuilding the tab would be a regression.
        const menuItems = computed(() => [
            {
                label: t("contact.detail.consents.viewHistory"),
                icon: "ri-eye-line",
                testId: "consent-row-history",
                onClick: () => emit("view-history"),
            },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_unref(IconShape), {
                    size: "md",
                    type: "square",
                    variant: "outlined",
                    color: "white",
                    icon: addressIcon.value
                }, null, 8 /* PROPS */, ["icon"]),
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(_unref(Badge), {
                                type: "with-dot",
                                theme: "dark",
                                size: "lg",
                                label: __props.qualifier,
                                "show-left-icon": false,
                                "data-testid": "consent-row-qualifier"
                            }, null, 8 /* PROPS */, ["label"]),
                            (__props.isPrimary)
                                ? (_openBlock(), _createBlock(_unref(Badge), {
                                    key: 0,
                                    theme: "yellow",
                                    size: "sm",
                                    label: _unref(t)('contact.detail.consents.primary'),
                                    "show-close-icon": false,
                                    "data-testid": "consent-row-primary",
                                    "left-icon": "ri-star-line"
                                }, null, 8 /* PROPS */, ["label"]))
                                : _createCommentVNode("v-if", true)
                        ]),
                        _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("h4", {
                                class: "truncate text-base leading-5 font-bold text-(--text-text-heading)",
                                title: __props.value,
                                "data-testid": "consent-row-value"
                            }, _toDisplayString(__props.value), 9 /* TEXT, PROPS */, _hoisted_6),
                            (displayedPurposes.value.length)
                                ? (_openBlock(), _createBlock(ConsentPurposeBadgeLine, {
                                    key: 0,
                                    purposes: displayedPurposes.value
                                }, null, 8 /* PROPS */, ["purposes"]))
                                : _createCommentVNode("v-if", true)
                        ])
                    ]),
                    _createElementVNode("div", _hoisted_7, [
                        (__props.canEdit)
                            ? (_openBlock(), _createBlock(_unref(Button), {
                                key: 0,
                                variant: "secondary",
                                size: "sm",
                                "icon-left": "ri-pencil-line",
                                label: _unref(t)('contact.detail.consents.edit'),
                                "data-testid": "consent-row-edit",
                                onClick: _cache[0] || (_cache[0] = ($event) => (emit('edit')))
                            }, null, 8 /* PROPS */, ["label"]))
                            : _createCommentVNode("v-if", true),
                        _createVNode(_unref(Dropdown), {
                            label: "",
                            "menu-align": "right",
                            items: menuItems.value,
                            "trigger-icon-left": "ri-more-2-fill",
                            "aria-label": _unref(t)('contact.detail.consents.rowActions')
                        }, null, 8 /* PROPS */, ["items", "aria-label"])
                    ])
                ])
            ]));
        };
    }
});
